import React, { useState, useEffect, useRef } from 'react';
import { X, Info, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import api from '../api/client';
import './RaceResultsViewer.css';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CollisionDetail {
  collider: 'Car' | 'Wall' | 'Object' | string;
  hasDamage: boolean;
  impactKmh: number | null;
  serverTimestampMs: number | null;
}

interface IngamePenalty {
  type: string;
  timeMs: number | null;
  investigationType: string | null;
  isPending: boolean;
  clearedAtMs: number | null;
}

interface LapDetail {
  lapNumber: number;
  sector1: number | null;
  sector2: number | null;
  sector3: number | null;
  lapTime: number | null;
  isInvalid: boolean;
  flags: string[];
  collisions: CollisionDetail[];
  ingamePenalties: IngamePenalty[];
}

interface DriverLapDetails {
  driverName: string;
  carModel: string;
  raceNumber: string | number | null;
  laps: LapDetail[];
}

interface PenaltyDetail {
  id: string;
  resultFilename: string;
  driverId?: string;
  driverName: string;
  raceNumber?: string | number;
  seconds: number;
  reason: string;
  createdAt: number;
}

interface RaceStanding {
  position: number;
  positionChange?: number | null;
  driverId?: string | number;
  driverName: string;
  carModel: string;
  raceNumber: string | number;
  bestLapTime: number | null;
  totalTime: number | null;
  rawTotalTime: number | null;
  adjustedTotalTime: number | null;
  penaltySeconds: number;
  penalties: PenaltyDetail[];
  lapCount: number;
  gap: string | number;
}

interface ProgressionDriver {
  driverId: number;
  name: string;
  positions: Array<{ lap: number; position: number }>;
}

interface ProgressionData {
  sessionType: string;
  drivers: ProgressionDriver[];
  lapCount: number;
}

interface RaceResultsViewerProps {
  sessionId: number;
  onClose: () => void;
  fetchResult?: () => Promise<any>;
}

// ─── Constants ──────────────────────────────────────────────────────────────

// bg is used for solid-colour flags; pattern flags are handled by CSS class only.
// VALID and INVALID are not shown as icons — they colour the lap row instead.
const FLAG_COLORS: Record<string, { bg?: string; label: string; pattern?: boolean }> = {
  WHITE:              { bg: '#e2e8f0', label: 'White' },
  GREEN:              { bg: '#22c55e', label: 'Green' },
  RED:                { bg: '#ef4444', label: 'Red' },
  BLUE:               { bg: '#3b82f6', label: 'Blue' },
  YELLOW:             { bg: '#eab308', label: 'Yellow' },
  CHECKERED:          { label: 'Checkered', pattern: true },
  BLACK_WHITE:        { label: 'Black/White', pattern: true },
  ORANGE_CIRCLE:      { label: 'Orange Circle', pattern: true },
  RED_YELLOW_STRIPES: { label: 'Red/Yellow Stripes', pattern: true },
  PITSTOP:            { label: 'Pit stop', pattern: true },
};

const PENALTY_LABELS: Record<string, string> = {
  PenaltyType_Warning:                   'Warning',
  PenaltyType_Time_Penalty_Seconds:      'Time Penalty',
  PenaltyType_Drive_Through:             'Drive Through',
  PenaltyType_Stop_And_Go:               'Stop & Go',
  PenaltyType_Disqualification:          'DSQ',
  PenaltyType_MP_TeleportToPit:          'Teleport to Pit',
  PenaltyType_Black_Flag_With_Slow_Down: 'Slow Down',
};

const CHART_COLORS = [
  '#22d3ee', '#34d399', '#fb923c', '#a78bfa',
  '#f472b6', '#facc15', '#60a5fa', '#4ade80',
  '#f87171', '#c084fc',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatTime = (seconds: number | null): string => {
  if (seconds === null) return '-';
  const totalSeconds = Math.floor(seconds);
  const milliseconds = Math.round((seconds - totalSeconds) * 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}:${String(secs).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
  }
  return `${secs}.${String(milliseconds).padStart(3, '0')}`;
};

const penaltyLabel = (type: string): string => PENALTY_LABELS[type] || type.replace(/^PenaltyType_/, '').replace(/_/g, ' ');

// ─── Sub-components ──────────────────────────────────────────────────────────

const FLAG_CLASS: Record<string, string> = {
  CHECKERED:          'flag-checkered',
  BLACK_WHITE:        'flag-black-white',
  ORANGE_CIRCLE:      'flag-orange-circle',
  RED_YELLOW_STRIPES: 'flag-red-yellow-stripes',
  PITSTOP:            'flag-pitstop',
};

const FlagIcon: React.FC<{ flag: string }> = ({ flag }) => {
  const meta = FLAG_COLORS[flag];
  if (!meta) return null;
  const patternClass = FLAG_CLASS[flag] ?? '';
  return (
    <span
      className={`flag-icon ${patternClass}`}
      style={meta.bg ? { background: meta.bg } : undefined}
      title={meta.label}
    />
  );
};

const CollisionBadge: React.FC<{ col: CollisionDetail }> = ({ col }) => {
  const colourClass = col.hasDamage ? 'collision-damage' : 'collision-no-damage';
  const title = [
    `${col.collider} impact`,
    col.impactKmh != null ? `${col.impactKmh.toFixed(1)} km/h` : null,
    col.hasDamage ? 'DAMAGE' : 'no damage',
  ].filter(Boolean).join(' · ');
  return (
    <span className={`collision-badge ${colourClass}`} title={title}>
      <AlertTriangle size={11} />
      {col.collider[0]}
    </span>
  );
};

const PenaltyBadge: React.FC<{ pen: IngamePenalty }> = ({ pen }) => {
  const label = penaltyLabel(pen.type);
  const timeLabel = pen.timeMs != null && pen.timeMs > 0 ? ` +${pen.timeMs / 1000}s` : '';
  const title = [
    label + timeLabel,
    pen.investigationType ? `Investigation: ${pen.investigationType}` : null,
    pen.isPending ? 'Pending' : 'Cleared',
  ].filter(Boolean).join(' · ');
  return (
    <span className={`ingame-penalty-badge ${pen.isPending ? 'pending' : 'cleared'}`} title={title}>
      {label}{timeLabel}
    </span>
  );
};

// ─── Progression Chart ────────────────────────────────────────────────────────

const ProgressionChart: React.FC<{ data: ProgressionData }> = ({ data }) => {
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  const svgRef = useRef<SVGSVGElement>(null);

  const toggle = (id: number) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { drivers, lapCount } = data;
  if (!drivers.length || lapCount < 2) {
    return <p className="lap-details-empty">Not enough lap data for progression chart.</p>;
  }

  const driverCount = drivers.length;
  const W = 680, H = 300, PAD = { top: 20, right: 16, bottom: 36, left: 32 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  // lap 0 = left edge, lapCount = right edge
  const xScale = (lap: number) => (lap / Math.max(lapCount, 1)) * chartW;
  const yScale = (pos: number) => ((pos - 1) / Math.max(driverCount - 1, 1)) * chartH;

  return (
    <div className="progression-chart-container">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="progression-svg"
        aria-label="Race position progression chart"
      >
        {/* Y-axis grid lines */}
        {Array.from({ length: driverCount }, (_, i) => (
          <g key={i}>
            <line
              x1={PAD.left} x2={PAD.left + chartW}
              y1={PAD.top + yScale(i + 1)} y2={PAD.top + yScale(i + 1)}
              stroke="rgba(148,163,184,0.12)" strokeWidth={1}
            />
            <text x={PAD.left - 6} y={PAD.top + yScale(i + 1) + 4} textAnchor="end" className="chart-axis-label">
              {i + 1}
            </text>
          </g>
        ))}

        {/* X-axis lap labels — lap 0 is intentionally unlabelled (grid position) */}
        {Array.from({ length: Math.min(lapCount, 20) }, (_, i) => {
          const lap = Math.round(1 + (i / Math.min(lapCount - 1, 19)) * (lapCount - 1));
          return (
            <text
              key={lap}
              x={PAD.left + xScale(lap)}
              y={H - PAD.bottom + 16}
              textAnchor="middle"
              className="chart-axis-label"
            >
              {lap}
            </text>
          );
        })}
        {/* "Grid" label at x=0 */}
        <text
          x={PAD.left}
          y={H - PAD.bottom + 16}
          textAnchor="middle"
          className="chart-axis-label chart-axis-label-grid"
        >
          Grid
        </text>

        {/* Driver lines */}
        {drivers.map((driver, idx) => {
          if (hidden.has(driver.driverId)) return null;
          const color = CHART_COLORS[idx % CHART_COLORS.length];
          const pts = driver.positions.map((p) =>
            `${PAD.left + xScale(p.lap)},${PAD.top + yScale(p.position)}`
          ).join(' ');
          if (!pts) return null;
          return (
            <polyline
              key={driver.driverId}
              points={pts}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.9}
            />
          );
        })}

        {/* Axis borders */}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="rgba(148,163,184,0.3)" strokeWidth={1} />
        <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH} stroke="rgba(148,163,184,0.3)" strokeWidth={1} />
      </svg>

      {/* Legend */}
      <div className="progression-legend">
        {drivers.map((driver, idx) => {
          const color = CHART_COLORS[idx % CHART_COLORS.length];
          const isHidden = hidden.has(driver.driverId);
          return (
            <button
              key={driver.driverId}
              type="button"
              className={`progression-legend-item ${isHidden ? 'hidden' : ''}`}
              onClick={() => toggle(driver.driverId)}
            >
              <span className="legend-dot" style={{ background: isHidden ? 'transparent' : color, border: `2px solid ${color}` }} />
              {driver.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const RaceResultsViewer: React.FC<RaceResultsViewerProps> = ({ sessionId, onClose, fetchResult }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'results' | 'progression'>('results');
  const [selectedDriver, setSelectedDriver] = useState<RaceStanding | null>(null);
  const [driverLaps, setDriverLaps] = useState<DriverLapDetails | null>(null);
  const [driverLapsLoading, setDriverLapsLoading] = useState(false);
  const [driverLapsError, setDriverLapsError] = useState<string | null>(null);
  const [progressionData, setProgressionData] = useState<ProgressionData | null>(null);
  const [progressionLoading, setProgressionLoading] = useState(false);
  const [data, setData] = useState<{
    sessionType: string;
    trackName: string;
    serverName: string;
    isRaceSession: boolean;
    leaderLapCount: number;
    standings: RaceStanding[];
  } | null>(null);

  useEffect(() => {
    const loadResults = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = fetchResult ? await fetchResult() : await api.getResultsSession(sessionId.toString());
        setData(res.data);
      } catch (err) {
        console.error('Failed to load race results:', err);
        setError('Failed to load race results');
      } finally {
        setLoading(false);
      }
    };
    loadResults();
  }, [sessionId, fetchResult]);

  const handleTabChange = async (tab: 'results' | 'progression') => {
    setActiveTab(tab);
    if (tab === 'progression' && !progressionData && !progressionLoading) {
      setProgressionLoading(true);
      try {
        const res = await api.getSessionProgression(sessionId);
        setProgressionData(res.data);
      } catch (err) {
        console.error('Failed to load progression:', err);
      } finally {
        setProgressionLoading(false);
      }
    }
  };

  const handleDriverRowClick = async (standing: RaceStanding) => {
    if (!standing.driverId) return;
    setSelectedDriver(standing);
    setDriverLapsLoading(true);
    setDriverLapsError(null);
    setDriverLaps(null);
    try {
      const res = await api.getDriverLaps(sessionId.toString(), standing.driverId, standing.carModel);
      setDriverLaps(res.data);
    } catch (err) {
      console.error('Failed to load driver laps:', err);
      setDriverLapsError('Unable to load lap breakdown.');
    } finally {
      setDriverLapsLoading(false);
    }
  };

  const renderGap = (standing: RaceStanding): string => {
    if (typeof standing.gap === 'string') return standing.gap;
    return String(formatTime(standing.gap));
  };

  const overallBestLap = data?.standings.reduce<number | null>((best, s) => {
    if (s.bestLapTime === null || s.bestLapTime <= 0) return best;
    return best === null || s.bestLapTime < best ? s.bestLapTime : best;
  }, null) ?? null;

  // Sector/lap highlight computation
  const lapRowsWithHighlights = (() => {
    if (!driverLaps?.laps?.length) return [] as (LapDetail & {
      isBestSoFarSector1: boolean; isBestSoFarSector2: boolean; isBestSoFarSector3: boolean; isBestSoFarLap: boolean;
      isOverallBestSector1: boolean; isOverallBestSector2: boolean; isOverallBestSector3: boolean; isOverallBestLapTime: boolean;
    })[];

    let bS1: number | null = null, bS2: number | null = null, bS3: number | null = null, bLap: number | null = null;
    const obS1 = driverLaps.laps.reduce<number | null>((b, l) => l.sector1 === null ? b : b === null || l.sector1 < b ? l.sector1 : b, null);
    const obS2 = driverLaps.laps.reduce<number | null>((b, l) => l.sector2 === null ? b : b === null || l.sector2 < b ? l.sector2 : b, null);
    const obS3 = driverLaps.laps.reduce<number | null>((b, l) => l.sector3 === null ? b : b === null || l.sector3 < b ? l.sector3 : b, null);
    const obLap = driverLaps.laps.reduce<number | null>((b, l) => l.lapTime === null ? b : b === null || l.lapTime < b ? l.lapTime : b, null);

    return driverLaps.laps.map((lap) => {
      const bs1 = lap.sector1 !== null && (bS1 === null || lap.sector1 < bS1);
      const bs2 = lap.sector2 !== null && (bS2 === null || lap.sector2 < bS2);
      const bs3 = lap.sector3 !== null && (bS3 === null || lap.sector3 < bS3);
      const bsLap = lap.lapTime !== null && (bLap === null || lap.lapTime < bLap);
      if (bs1) bS1 = lap.sector1;
      if (bs2) bS2 = lap.sector2;
      if (bs3) bS3 = lap.sector3;
      if (bsLap) bLap = lap.lapTime;
      return {
        ...lap,
        isBestSoFarSector1: bs1, isBestSoFarSector2: bs2, isBestSoFarSector3: bs3, isBestSoFarLap: bsLap,
        isOverallBestSector1: lap.sector1 !== null && obS1 !== null && lap.sector1 === obS1,
        isOverallBestSector2: lap.sector2 !== null && obS2 !== null && lap.sector2 === obS2,
        isOverallBestSector3: lap.sector3 !== null && obS3 !== null && lap.sector3 === obS3,
        isOverallBestLapTime: lap.lapTime !== null && obLap !== null && lap.lapTime === obLap,
      };
    });
  })();

  if (loading) {
    return (
      <div className="race-results-modal-overlay">
        <div className="race-results-modal">
          <div className="modal-header">
            <h2>Loading Results...</h2>
            <button className="close-button" onClick={onClose}><X size={24} /></button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="race-results-modal-overlay">
        <div className="race-results-modal">
          <div className="modal-header">
            <h2>Error</h2>
            <button className="close-button" onClick={onClose}><X size={24} /></button>
          </div>
          <div className="error-message">{error || 'No data available'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="race-results-modal-overlay" onClick={onClose}>
      <div className="race-results-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title">
            <h2>{data.sessionType.charAt(0).toUpperCase() + data.sessionType.slice(1)} Results</h2>
            <div className="session-info">
              <span className="track-name">{data.trackName}</span>
              <span className="server-name">{data.serverName}</span>
              {data.isRaceSession && <span className="lap-info">{data.leaderLapCount} laps</span>}
            </div>
          </div>
          <div className="modal-actions">
            <button className="close-button" onClick={onClose}><X size={24} /></button>
          </div>
        </div>

        {/* Tab bar — only show Race Progression for race sessions */}
        {data.isRaceSession && (
          <div className="results-tab-bar">
            <button
              type="button"
              className={`results-tab ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => void handleTabChange('results')}
            >
              Results
            </button>
            <button
              type="button"
              className={`results-tab ${activeTab === 'progression' ? 'active' : ''}`}
              onClick={() => void handleTabChange('progression')}
            >
              Race Progression
            </button>
          </div>
        )}

        {/* ── Tab: Race Progression ── */}
        {activeTab === 'progression' && (
          <div className="progression-tab-content">
            {progressionLoading && <p className="lap-details-empty">Loading progression data…</p>}
            {!progressionLoading && progressionData && <ProgressionChart data={progressionData} />}
          </div>
        )}

        {/* ── Tab: Results ── */}
        {activeTab === 'results' && (
          <>
            {data.standings.some((s) => s.penalties?.length > 0) && (
              <div className="penalty-summary-bar">
                <strong>Penalties applied:</strong> {data.standings.reduce((n, s) => n + (s.penalties?.length ?? 0), 0)} total
              </div>
            )}

            <div className="results-table-container">
              <table className="results-standings-table">
                <thead>
                  <tr>
                    <th className="col-position">Pos</th>
                    <th className="col-driver">Driver</th>
                    <th className="col-car">Car</th>
                    <th className="col-number">#</th>
                    <th className="col-best">Best Lap</th>
                    {data.isRaceSession && <th className="col-time">Total Time</th>}
                    {data.isRaceSession && <th className="col-penalty">Penalty</th>}
                    {data.isRaceSession && <th className="col-laps">Laps</th>}
                    <th className="col-gap">Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {data.standings.map((standing) => {
                    const isOverallBestLap = standing.bestLapTime !== null && overallBestLap !== null && standing.bestLapTime === overallBestLap;
                    const pc = standing.positionChange;
                    return (
                      <tr
                        key={`${standing.position}-${standing.driverName}`}
                        className={`standing-row ${standing.position === 1 ? 'winner' : ''} ${isOverallBestLap ? 'best-lap' : ''}`}
                        onClick={() => standing.driverId && handleDriverRowClick(standing)}
                        style={{ cursor: standing.driverId ? 'pointer' : 'default' }}
                      >
                        <td className="col-position"><span className="position-badge">{standing.position}</span></td>
                        <td className="col-driver">
                          <div className="driver-name-row">
                            <span className="driver-name"><span className="driverPos">{standing.position}.</span> {standing.driverName}</span>
                            {pc !== null && pc !== undefined && data.isRaceSession && (
                              <span className={`position-change ${pc > 0 ? 'gain' : pc < 0 ? 'loss' : 'equal'}`}>
                                {pc > 0 ? <ArrowUp size={12} /> : pc < 0 ? <ArrowDown size={12} /> : <span className="equal-mark">=</span>}
                                {pc !== 0 && <span>{Math.abs(pc)}</span>}
                              </span>
                            )}
                          </div>
                          <span className="car-model">{standing.carModel}</span>
                        </td>
                        <td className="col-car"><span className="car-model">{standing.carModel}</span></td>
                        <td className="col-number"><span className="race-number">{standing.raceNumber}</span></td>
                        <td className="col-best">
                          <span className={`lap-best ${isOverallBestLap ? 'best' : ''}`}>{formatTime(standing.bestLapTime)}</span>
                        </td>
                        {data.isRaceSession && (
                          <td className="col-time">
                            <span className="penalty-cell">
                              <span className="lap-time">{formatTime(standing.totalTime)}</span>
                              {standing.penalties?.length > 0 && (
                                <span className="penalty-info" tabIndex={0} aria-label="Penalty reason">
                                  <Info size={12} />
                                  <span className="penalty-tooltip">
                                    <strong>+{Math.round(standing.penaltySeconds ?? 0)}s </strong>
                                    {standing.penalties?.map((p) => p.reason).join('\n')}
                                  </span>
                                </span>
                              )}
                            </span>
                          </td>
                        )}
                        {data.isRaceSession && (
                          <td className="col-penalty">
                            {standing.penalties?.length > 0 ? (
                              <span className="penalty-cell">
                                <span className="penalty-value">+{Math.round(standing.penaltySeconds ?? 0)}s</span>
                                <span className="penalty-info" tabIndex={0} aria-label="Penalty reason">
                                  <Info size={14} />
                                  <span className="penalty-tooltip">{standing.penalties?.map((p) => p.reason).join('\n')}</span>
                                </span>
                              </span>
                            ) : (
                              <span className="no-penalty">—</span>
                            )}
                          </td>
                        )}
                        {data.isRaceSession && (
                          <td className="col-laps"><span className="lap-count">{standing.lapCount}</span></td>
                        )}
                        <td className="col-gap">
                          <span className={`gap ${standing.position === 1 ? 'leader' : ''}`}>{renderGap(standing)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── Driver lap breakdown modal ── */}
        {selectedDriver && (
          <div className="lap-details-modal-overlay" onClick={() => { setSelectedDriver(null); setDriverLaps(null); setDriverLapsError(null); }}>
            <div className="lap-details-modal" onClick={(e) => e.stopPropagation()}>
              <div className="lap-details-header">
                <div>
                  <h3>{selectedDriver.driverName}</h3>
                  <p>{selectedDriver.carModel}{selectedDriver.raceNumber ? ` · #${selectedDriver.raceNumber}` : ''}</p>
                </div>
                <button className="close-button" onClick={() => { setSelectedDriver(null); setDriverLaps(null); setDriverLapsError(null); }}>
                  <X size={20} />
                </button>
              </div>

              {driverLapsLoading && <div className="lap-details-empty">Loading lap breakdown...</div>}
              {driverLapsError && <div className="lap-details-empty error">{driverLapsError}</div>}

              {!driverLapsLoading && !driverLapsError && (
                <div className="lap-details-table-wrapper">
                  <table className="lap-details-table">
                    <thead>
                      <tr>
                        <th>Lap</th>
                        <th>Sector 1</th>
                        <th>Sector 2</th>
                        <th>Sector 3</th>
                        <th>Lap Time</th>
                        <th>Flags</th>
                        <th>Events</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lapRowsWithHighlights.map((lap) => {
                        const hasEvents = lap.collisions.length > 0 || lap.ingamePenalties.length > 0;
                        return (
                          <tr key={`${selectedDriver.driverId}-${lap.lapNumber}`} className={lap.isInvalid ? 'lap-row-invalid' : ''}>
                            <td>{lap.lapNumber}</td>
                            <td>
                              <span className={`lap-value ${lap.isInvalid ? 'invalid' : lap.isOverallBestSector1 ? 'overall-best' : lap.isBestSoFarSector1 ? 'best-so-far' : ''}`}>
                                {lap.sector1 === null ? '—' : formatTime(lap.sector1)}
                              </span>
                            </td>
                            <td>
                              <span className={`lap-value ${lap.isInvalid ? 'invalid' : lap.isOverallBestSector2 ? 'overall-best' : lap.isBestSoFarSector2 ? 'best-so-far' : ''}`}>
                                {lap.sector2 === null ? '—' : formatTime(lap.sector2)}
                              </span>
                            </td>
                            <td>
                              <span className={`lap-value ${lap.isInvalid ? 'invalid' : lap.isOverallBestSector3 ? 'overall-best' : lap.isBestSoFarSector3 ? 'best-so-far' : ''}`}>
                                {lap.sector3 === null ? '—' : formatTime(lap.sector3)}
                              </span>
                            </td>
                            <td>
                              <span className={`lap-value ${lap.isInvalid ? 'invalid' : lap.isOverallBestLapTime ? 'overall-best' : lap.isBestSoFarLap ? 'best-so-far' : ''}`}>
                                {lap.lapTime === null ? '—' : formatTime(lap.lapTime)}
                              </span>
                            </td>
                            <td>
                              <div className="lap-flags">
                                {lap.flags.length > 0
                                  ? lap.flags.map((f) => <FlagIcon key={f} flag={f} />)
                                  : <span className="lap-value">—</span>}
                              </div>
                            </td>
                            <td>
                              {hasEvents ? (
                                <div className="lap-events">
                                  {lap.collisions.map((col, i) => <CollisionBadge key={i} col={col} />)}
                                  {lap.ingamePenalties.map((pen, i) => <PenaltyBadge key={i} pen={pen} />)}
                                </div>
                              ) : (
                                <span className="lap-value">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {!lapRowsWithHighlights.length && (
                        <tr><td colSpan={7} className="lap-details-empty">No lap data available.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RaceResultsViewer;
